/**
 * Administratörens förhandsvisningsläge ("Visa som").
 *
 * Två saker kan förhandsvisas, alltid skrivskyddat:
 * - En kontonivå: sajten beter sig som om du låg på Gratis, Plus eller Pro.
 * - En annan användares sidor: du ser deras annonser och nivå, men kan inte ändra något.
 *
 * Läget sparas per flik (sessionStorage) så att en vanlig omladdning behåller det,
 * men en ny flik alltid startar i ditt eget läge.
 */
import { useSyncExternalStore } from "react";
import type { PlanId } from "./plans";

export type AdminPreview = {
  /** Nivån sajten ska bete sig som, eller null för din egen. */
  plan: PlanId | null;
  /** Användaren vars sidor du tittar på, eller null. */
  user: { id: string; label: string } | null;
};

const KEY = "sellavo:admin-preview";
const EMPTY: AdminPreview = { plan: null, user: null };

let state: AdminPreview = EMPTY;
let loaded = false;
const listeners = new Set<() => void>();

function load() {
  if (loaded || typeof window === "undefined") return;
  loaded = true;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (raw) state = { ...EMPTY, ...(JSON.parse(raw) as Partial<AdminPreview>) };
  } catch {
    state = EMPTY;
  }
}

export function getAdminPreview(): AdminPreview {
  load();
  return state;
}

/** Nivån som datahämtningarna ska låtsas att du har, om något är valt. */
export function getPreviewPlan(): PlanId | null {
  return getAdminPreview().plan;
}

export function setAdminPreview(next: Partial<AdminPreview>) {
  load();
  state = { ...state, ...next };
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* privat läge – då gäller valet bara tills sidan laddas om */
  }
  for (const l of listeners) l();
}

export function clearAdminPreview() {
  setAdminPreview({ plan: null, user: null });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Läser förhandsvisningsläget i en komponent. */
export function useAdminPreview(): AdminPreview {
  return useSyncExternalStore(subscribe, getAdminPreview, () => EMPTY);
}
